/**
 * The kit every game of the thirty is built on (show-core `thirty.ts`), and the contract a board
 * keeps. NAV's five (nav/) are the reference: read nav/heading.tsx first (the smallest), then
 * nav/gaps.tsx (motion, and a demo worked out by the game's own rules).
 *
 * WHY (Marek, 6 Oct 2026, after the first real evening): the games were too hard; they did not look
 * like one set; the big screen showed only a progress bar; and nobody understood a game from its
 * little sketch. So: easier rounds (kit/levels.ts), one look (kit/tokens.ts, kit/frame.tsx), a board
 * drawn from a small state anywhere (the view split, kit/round.ts, kit/mirror.ts), and a how-to that
 * plays the real board with a ghost finger (kit/demo.tsx).
 *
 * ── WHAT A CONVERTED MODULE EXPORTS ─────────────────────────────────────────────────────────────
 *
 *   export const game = thirtyGame<MyView>({ Board, View, demo, words, mistakes? });
 *
 *  - `MyView`: the view state. Everything the board shows, and nothing else: plain numbers,
 *    strings, booleans, null and short arrays; its JSON under 300 bytes (the kit test checks every
 *    demo frame; `&mirror=1` shows the live size). Positions in board units (`BOARD`, 320 × 320,
 *    origin top left), rounded with `q()`. It always has `t`, the game clock in ms (`round.now`):
 *    anything that moves on its own (a twinkle, a sway, a scrolling sky) is drawn from `t`, never
 *    from a timer of the View's own, a CSS animation or SMIL. It has `fx?: Fx | undefined`
 *    (`round.fx`: the glow and the shake). Work out in the View what follows from the state (a
 *    needle's colour from whether it is on the mark) rather than sending it.
 *  - `View({ view, era, accent, lang, input })`: pure. It returns `<BoardFrame era accent status fx
 *    input>` with the board's SVG inside, drawn from `view` alone: no refs, no state, no effects,
 *    no Date.now or Math.random, no measuring. The same View draws the live board (with `input`),
 *    the how-to demo and the mirror (without). Gradient ids from `useSvgId()`. A demo glides every
 *    number between its keys, so read counts and states with `Math.round` (nav/gaps.tsx).
 *  - `Board(props: RoundProps)`: the logic. It keeps the round in refs, moves it in
 *    `useRound(props, (dt, now, won) => …)` (the game clock's loop; it never runs while the board is
 *    covered), builds the view each render and returns `<View view={round.show(view)} … input={…} />`.
 *    Touches arrive in board units through `input`: `down`/`move`/`up` on the board, `press(id)` from a
 *    `BoardButton`; check `round.live` first. A move that counts: `round.hit()`; a mistake:
 *    `round.miss()` (it calls onMistake); progress: `round.progress(0–1)`; the win: `round.win()`,
 *    once — the station hears of it `CLEAR_MS` later on the game clock. No setTimeout, no wall-clock
 *    time anywhere in a board.
 *  - `demo: DemoScript<MyView>`: the how-to loop, 6–8 s, played through the View (kit/types.ts):
 *      { ms: 7000,
 *        keys:   [{ at: 0, view: { …the whole first view… } }, { at: 900, view: { y: 140 } }, …],
 *        finger: [{ at: 0, x: 250, y: 280, hide: true }, { at: 400, x: 250, y: 280, hide: false },
 *                 { at: 900, x: 250, y: 280, down: true }, { at: 1050, x: 250, y: 280, down: false }, …],
 *        steps:  [{ at: 0, en: 'TAP TO FLY UP', no: 'TRYKK FOR Å FLY OPP' }, …] }
 *    Each key changes only what it names; numbers glide from the key before (`ease`, default
 *    linear; `'step'` holds them until the key); words, switches and lists that change length change
 *    at the key. The player fills in `t`. The finger's tip is at (x, y) in board units, its hand
 *    reaching up from the bottom right: `down: true` presses (a ripple), staying down holds, moving
 *    while down drags (a trail), `hide` fades it on or off. Two or three steps, capitals, at most 30
 *    characters, each lit while the finger does it. Show round 1. Where the game moves by rules (a
 *    flight, a fall), work the demo out once with the game's own functions and key it every 100 ms
 *    (nav/gaps.tsx `demoFlight`, nav/dock.tsx `demoLanding`); otherwise key it by hand
 *    (nav/heading.tsx, nav/stars.tsx).
 *  - `words` as before (instruction ≤ 90 characters; `howMove` stays for the screen texts).
 *  - Level tables wrapped in `easier([old round 1, old round 2])` (kit/levels.ts, done for all 29).
 *
 * ── THE LOOK (kit/tokens.ts, kit/frame.tsx) ─────────────────────────────────────────────────────
 *
 *  - Colours: `accent` (the era's, readable) for what to look at and what is yours; `OK` right;
 *    `BAD` wrong (the station's red); `WARN` careful; `INK.hi/text/dim/faint/onFill` for words and
 *    lines; `PALETTE` (six colour-blind-safe colours) when things must be told apart; the era's own
 *    colours (`ERA_SKIN`) for scenery and art. No other reds, greens or ambers; `alpha()` for a tint.
 *  - `mono` and `sans` from the kit, never as strings; sizes from `TYPE`; lines from `STROKE`;
 *    corners from `RADIUS`.
 *  - The status line, always above the board: `{ text: 'GATES', count: [done, of], hint: 'TAP TO
 *    FLY' }` while it goes on (the count is what is done of the whole: GATES 2 / 4); `{ text:
 *    '✓ THROUGH', tone: 'ok' }` once won; `{ text: 'NOT THAT ONE', tone: 'bad' }` just after a
 *    mistake; `tone: 'warn'` for careful. Capitals, EN and NO; the hint hides on a narrow board.
 *  - Buttons inside the board: `BoardButton` (at least 44 units tall), nothing hand-styled.
 *  - Feedback: `round.hit()` glows, `round.miss()` shakes and reddens the edge; the frame draws
 *    both from `fx`. The clear lasts `CLEAR_MS`; draw an outro from `round.clear` (0–1) if you like.
 *
 * ── HOW TO TEST ─────────────────────────────────────────────────────────────────────────────────
 *
 *  - The practice page: `cd apps/guest && pnpm exec vite --port <free port> --strictPort`, then
 *    /practice.html?game=nav.dock (the how-to card first, then the board). `&mirror=1` draws the
 *    board a second time, small, from its view state alone, through JSON (as the TV will), with
 *    the state's size: if the copy differs from the board, the View is not pure. `&demoAt=2300`
 *    holds the demo still at that moment of its loop.
 *  - At 360 × 560 and 390 × 844: the card with its demo and START on one screen; the board whole.
 *  - `test/thirty-kit.test.ts` checks every converted module: the demo's length, keys and steps,
 *    every frame drawn the same twice by the View in every era and both languages, and the view's
 *    size. Your post's test file keeps the rules (`npx vitest run apps/guest/test/thirty-nav.test.ts`
 *    from the repo root).
 *  - The test bench's autopilot plays a game through ThirtyGameView's callbacks (onProgress,
 *    onMistake, onWin), not through the board, and presses the card's last button (START): a
 *    converted board needs nothing for it, and the card must keep START its last button.
 *
 * A board not yet converted keeps its old `Board` and `Demo` (an SVG sketch with `Tap`, `Shown`,
 * `DemoSvg`, `Stage`, `Readout`, `localPoint`); those go once every board is converted.
 *
 * The house rules for every board, as before:
 *  - one screen on a phone held at a dinner table, in low light, often with one hand;
 *  - big targets (44 units at the very least), plain words, nothing to read while it moves;
 *  - nothing flashes: a light may pulse at most about twice a second, and energy comes from
 *    motion, never from brightness;
 *  - the board stands still while `paused` (an alarm or a sheet covers it) and ignores taps;
 *  - drawn in code (SVG), no images to load over the venue's Wi-Fi;
 *  - round 1 anyone finishes; the whole game about 30–60 s.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { PlayTask } from '@continuum/show-core';
import type { Lang } from '../../ui/text.js';
import { FitToScreen } from '../../ui/FitToScreen.js';
import { ERA_SKIN, EraBackdrop } from '../art.js';
import type { Era } from '../art.js';
import type { GameProps } from '../types.js';
import type { Level, ThirtyModule } from './kit/types.js';
import { useFrame, useGameTime } from './kit/clock.js';
import { BAD, INK, alpha, mono, readable, sans } from './kit/tokens.js';
import { MIRROR_MS, throttle } from './kit/mirror.js';
import type { MirrorFrame } from './kit/mirror.js';

export { easier, easierNumbers, halfway } from './kit/levels.js';
export { say, seeded, useFrame, useGameTime, useTicker } from './kit/clock.js';
export { BAD, BOARD, INK, OK, PALETTE, RADIUS, STROKE, TYPE, WARN, accentOf, alpha, clamp, mono, q, readable, sans } from './kit/tokens.js';
export { BoardButton, BoardFrame, FrameOverlay, StatusLine, useSvgId } from './kit/frame.js';
export type { ButtonTone, Status, Tone } from './kit/frame.js';
export { CLEAR_MS, HIT_MS, MISS_MS, useRound } from './kit/round.js';
export type { Fx, Round } from './kit/round.js';
export { DemoPlayer, DemoSteps, GhostFinger, fingerAt, mix, stepAt, viewAt } from './kit/demo.js';
export { MIRROR_MS, throttle } from './kit/mirror.js';
export type { MirrorFrame } from './kit/mirror.js';
export { thirtyGame } from './kit/types.js';
export type {
  BoardInput,
  DemoKey,
  DemoScript,
  DemoStep,
  Ease,
  FingerKey,
  Level,
  Pt,
  RoundProps,
  ThirtyGame,
  ThirtyModule,
  ThirtyWords,
  ViewComponent,
  ViewProps,
} from './kit/types.js';

// ── the old kit, for boards not yet converted ──────────────────────────────────────────────────

/**
 * The play surface: the era's colours, its slow backdrop behind, a soft vignette and an inner
 * edge in the accent. `flash` is the shared language: 'hit' warms the board for a moment,
 * 'miss' reddens the edge.
 */
export function Stage({
  era,
  accent,
  height,
  flash = null,
  children,
  style,
}: {
  era: Era;
  accent: string;
  height: number;
  flash?: 'hit' | 'miss' | null;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const skin = ERA_SKIN[era];
  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: 16,
        overflow: 'hidden',
        background: `radial-gradient(120% 90% at 50% 0%, ${skin.ink}1f, transparent 60%), linear-gradient(180deg, ${skin.bg}, #040609)`,
        border: `1px solid ${flash === 'miss' ? BAD : `${accent}44`}`,
        boxShadow: `inset 0 0 0 1px rgba(255,255,255,.03), inset 0 -40px 60px rgba(0,0,0,.45)${flash === 'hit' ? `, inset 0 0 40px ${accent}40` : ''}`,
        transition: 'border-color .12s, box-shadow .18s',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <EraBackdrop era={era} opacity={0.35} />
      {children}
    </div>
  );
}

/** One line of status above or below a board: big, mono, in the accent. */
export function Readout({ children, color, align = 'center' }: { children: ReactNode; color: string; align?: 'center' | 'left' }) {
  return (
    <div style={{ fontFamily: mono, fontSize: 15, fontWeight: 700, letterSpacing: 1.4, color, textAlign: align, minHeight: 20 }}>
      {children}
    </div>
  );
}

/** A finger's tap in a demo: a ring that opens and fades, at each of `at` (fractions of the loop). */
export function Tap({ x, y, at, dur, color = '#dfe9f2' }: { x: number; y: number; at: readonly number[]; dur: number; color?: string }) {
  const keys: number[] = [0];
  const r: number[] = [0];
  const o: number[] = [0];
  for (const t of at) {
    keys.push(t, Math.min(0.999, t + 0.01), Math.min(0.9995, t + 0.14));
    r.push(0, 4, 17);
    o.push(0, 0.9, 0);
  }
  keys.push(1);
  r.push(0);
  o.push(0);
  const kt = keys.map((k) => k.toFixed(4)).join(';');
  return (
    <circle cx={x} cy={y} r="0" fill="none" stroke={color} strokeWidth="2.5" opacity="0">
      <animate attributeName="r" values={r.join(';')} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
      <animate attributeName="opacity" values={o.join(';')} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
    </circle>
  );
}

/** Shows its children between `from` and `to` (fractions of the loop), hidden otherwise. */
export function Shown({ from, to, dur, children, base = 0 }: { from: number; to: number; dur: number; children: ReactNode; base?: number }) {
  const kt = [0, from, Math.min(0.999, from + 0.01), to, Math.min(0.9995, to + 0.01), 1].map((k) => k.toFixed(4)).join(';');
  return (
    <g opacity={base}>
      <animate attributeName="opacity" values={`${base};${base};1;1;${base};${base}`} keyTimes={kt} dur={`${dur}s`} repeatCount="indefinite" />
      {children}
    </g>
  );
}

/**
 * Where a touch lands on `el`, in the board's own layout pixels, and the board's own size. Right
 * however the game is scaled down to fit a short screen (ui/FitToScreen.tsx): the box a browser
 * measures on screen is the scaled one, the board draws in its own.
 */
export function localPoint(el: Element, e: { clientX: number; clientY: number }): { x: number; y: number; w: number; h: number } {
  const box = el.getBoundingClientRect();
  const w = (el as HTMLElement).offsetWidth || box.width;
  const h = (el as HTMLElement).offsetHeight || box.height;
  return { x: ((e.clientX - box.left) * w) / (box.width || 1), y: ((e.clientY - box.top) * h) / (box.height || 1), w, h };
}

/** The demo's frame: a 220 × 120 SVG. */
export function DemoSvg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 220 120" style={{ display: 'block', width: '100%', height: 'auto' }} aria-hidden="true">
      {children}
    </svg>
  );
}

// ── the three rounds, as the station plays them ────────────────────────────────────────────────

const ROUND_WORDS = {
  en: { round: (n: number, of: number) => `ROUND ${n}/${of}`, next: (n: number) => `ROUND ${n}` },
  no: { round: (n: number, of: number) => `RUNDE ${n}/${of}`, next: (n: number) => `RUNDE ${n}` },
} as const;

/** The pause between rounds. */
const BREAK_MS = 1_400;

/**
 * One game of the thirty, as the station plays it: the board three times, a level up each time.
 * A new round is a fresh board (its own `key`), so every round starts clean. With `mirror` on, the
 * board's view state goes to `onMirror` a few times a second (kit/mirror.ts).
 */
export function ThirtyGameView({
  game,
  task,
  era,
  accent,
  onProgress,
  onWin,
  onMistake,
  onFrame,
  paused = false,
  lang,
  mirror = false,
  onMirror,
}: GameProps<PlayTask> & {
  game: ThirtyModule;
  lang: Lang;
  /** Hand the board's view state out, for a screen that mirrors it. Off unless asked for. */
  mirror?: boolean;
  onMirror?: ((frame: MirrorFrame) => void) | undefined;
}) {
  const rounds = Math.max(1, game.rounds ?? 3);
  const [round, setRound] = useState(0);
  const [between, setBetween] = useState(false);
  const won = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake, onMirror });
  cbs.current = { onProgress, onWin, onFrame, onMistake, onMirror };
  const w = ROUND_WORDS[lang];

  // Every change of progress is a message to the room: only a new whole percent goes, not every frame.
  const sentPct = useRef(-1);
  const report = (pct: number) => {
    if (pct === sentPct.current) return;
    sentPct.current = pct;
    cbs.current.onProgress(pct);
  };
  const progress = useCallback(
    (fraction: number) => {
      const f = Math.min(1, Math.max(0, fraction));
      report(Math.round(((round + f) / rounds) * 100));
    },
    [round, rounds],
  );

  // The break's timer runs on the game's own clock, so a cover over the board holds it too.
  const time = useGameTime(paused);
  const breakEnds = useRef<number | null>(null);
  useFrame(between);
  useEffect(() => {
    if (!between || breakEnds.current === null || time() < breakEnds.current) return;
    breakEnds.current = null;
    setBetween(false);
    setRound((r) => r + 1);
  });

  const cleared = useCallback(() => {
    if (won.current || between) return;
    if (round + 1 >= rounds) {
      won.current = true;
      report(100);
      cbs.current.onWin();
      return;
    }
    report(Math.round(((round + 1) / rounds) * 100));
    breakEnds.current = time() + BREAK_MS;
    setBetween(true);
  }, [round, rounds, between, time]);

  useEffect(() => {
    cbs.current.onFrame(`r:${round}|b:${between ? 1 : 0}`);
  }, [round, between]);

  // The mirror: off unless asked for, and then about five looks a second.
  const where = useRef({ round, between, era });
  where.current = { round, between, era };
  const onView = useMemo(
    () =>
      mirror
        ? throttle(MIRROR_MS, (view: unknown) => {
            const at = where.current;
            cbs.current.onMirror?.({ g: task.game, e: at.era, r: at.round, b: at.between ? 1 : 0, v: view });
          })
        : undefined,
    [mirror, task.game],
  );

  const Board = game.Board;
  accent = readable(accent);
  // One screen on any phone: the rounds and the board scaled down just enough to fit a short one.
  return (
    <FitToScreen>
      <div style={{ marginTop: 14 }}>
        {rounds > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
            {Array.from({ length: rounds }, (_, r) => (
              <span
                key={r}
                style={{
                  fontFamily: mono,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: `1px solid ${r === round ? accent : alpha(accent, 0.27)}`,
                  color: r < round ? INK.onFill : r === round ? accent : INK.faint,
                  background: r < round ? accent : 'transparent',
                }}
              >
                {w.round(r + 1, rounds)}
              </span>
            ))}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <div style={{ opacity: between ? 0.25 : 1, transition: 'opacity .25s', pointerEvents: between ? 'none' : undefined }}>
            <Board
              key={round}
              level={Math.min(2, round) as Level}
              era={era}
              accent={accent}
              lang={lang}
              paused={paused || between}
              onCleared={cleared}
              onMistake={() => cbs.current.onMistake?.()}
              onProgress={progress}
              onView={onView}
            />
          </div>
          {between && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
                animation: 'ccFadeIn .25s ease-out',
              }}
            >
              <div
                style={{
                  fontFamily: sans,
                  fontSize: 30,
                  fontWeight: 800,
                  letterSpacing: 2,
                  color: INK.hi,
                  textAlign: 'center',
                  textShadow: `0 0 24px ${accent}`,
                }}
              >
                {w.next(round + 2)}
              </div>
            </div>
          )}
        </div>
      </div>
    </FitToScreen>
  );
}
